import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSeo, breadcrumbSchema } from "@/lib/seo";
import { Reveal } from "@/components/site/Reveal";
import api from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, Check, Loader2 } from "lucide-react";

function faqSchema(faqs = []) {
  return { "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) };
}

export function Loading() { return <div className="py-40 flex justify-center"><Loader2 className="animate-spin text-restrained-blue" /></div>; }

function Block({ block, accent }) {
  const { type } = block;
  if (type === "prose")
    return (
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">{block.overline && <p className="overline mb-3" style={{ color: accent }}>{block.overline}</p>}<h2 className="font-serif text-3xl md:text-4xl text-deep-navy leading-tight">{block.title}</h2></div>
        <div className="lg:col-span-7 lg:col-start-6"><p className="text-lg text-charcoal/75 leading-relaxed">{block.text}</p></div>
      </div>
    );
  if (type === "cards")
    return (
      <div>
        {block.overline && <p className="overline mb-2" style={{ color: accent }}>{block.overline}</p>}
        <h2 className="font-serif text-3xl md:text-4xl text-deep-navy mb-8">{block.title}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
          {block.items.map((it, i) => {
            const inner = (
              <div className="bg-white p-7 h-full group">
                <span className="inline-block h-1 w-8 mb-3" style={{ backgroundColor: accent }} />
                <h3 className="font-serif text-xl text-deep-navy">{it.name}</h3>
                {it.text && <p className="mt-2 text-sm text-charcoal/70 leading-relaxed">{it.text}</p>}
                {it.to && <span className="mt-4 inline-flex items-center gap-1 text-xs group-hover:gap-2 transition-all" style={{ color: accent }}>Explore <ArrowRight size={13} /></span>}
              </div>
            );
            return it.to ? <Link key={i} to={it.to} className="block hover:bg-off-white transition-colors">{inner}</Link> : <div key={i}>{inner}</div>;
          })}
        </div>
      </div>
    );
  if (type === "list")
    return (
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">{block.overline && <p className="overline mb-3" style={{ color: accent }}>{block.overline}</p>}<h2 className="font-serif text-3xl md:text-4xl text-deep-navy leading-tight">{block.title}</h2></div>
        <ul className="lg:col-span-7 lg:col-start-6 space-y-6">
          {block.items.map((it, i) => {
            const title = typeof it === "string" ? it : (it.title || it.t);
            const text = typeof it === "string" ? null : (it.text || it.d);
            return (
              <li key={i} className="flex gap-4 pb-6 border-b border-[#E5E5E5]">
                <span className="shrink-0 mt-1.5 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: `${accent}1A` }}><Check size={12} style={{ color: accent }} /></span>
                <div>
                  <p className="font-serif text-lg md:text-xl text-deep-navy leading-snug">{title}</p>
                  {text && <p className="mt-2 text-charcoal/70 leading-relaxed">{text}</p>}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    );
  if (type === "steps")
    return (
      <div>
        {block.overline && <p className="overline mb-2" style={{ color: accent }}>{block.overline}</p>}
        <h2 className="font-serif text-3xl md:text-4xl text-deep-navy mb-8">{block.title}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
          {block.items.map((s, i) => (
            <div key={i} className="bg-white p-5">
              <span className="font-mono text-sm" style={{ color: accent }}>{s.k}</span>
              <h4 className="font-serif text-lg text-deep-navy mt-2">{s.title}</h4>
              <p className="text-sm text-charcoal/70 mt-1 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </div>
    );
  return null;
}

export function EditorialPage({ data }) {
  const accent = "#5C2D50";
  const crumbs = data.breadcrumb || [{ name: data.eyebrow || "", path: data.seo.path }];
  useSeo({
    title: (data.seo.title || "").replace(/\s*\|\s*ThinkStack.*$/i, ""),
    description: data.seo.description,
    path: data.seo.path,
    image: data.seo.image || data.image || null,
    canonical: data.seo.canonical || null,
    robots: data.seo.robots || null,
    keywords: data.seo.keywords || null,
    schema: [
      { "@context": "https://schema.org", "@type": data.seo.schema_type || "Service", name: data.h1, description: data.seo.description, provider: { "@type": "Organization", name: "ThinkStack", areaServed: "Singapore" }, areaServed: "Singapore" },
      breadcrumbSchema([{ name: "Home", path: "/" }, ...crumbs]),
      faqSchema(data.faqs),
    ],
  });

  return (
    <div>
      <section className="relative text-white overflow-hidden" style={{ backgroundColor: "#0B1B34" }}>
        {data.image && <img src={data.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />}
        <div className="relative mx-auto max-w-7xl px-6 md:px-12 pt-16 pb-16 md:pt-20 md:pb-20">
          <nav className="text-xs text-white/70 mb-6 flex flex-wrap gap-2" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            {crumbs.map((c, i) => <span key={i}>/ <Link to={c.path} className="hover:text-white">{c.name}</Link></span>)}
          </nav>
          <Reveal>
            {data.eyebrow && <p className="overline text-white/60 mb-4">{data.eyebrow}</p>}
            <h1 className="font-serif text-4xl md:text-6xl tracking-tight leading-[1.02] max-w-4xl">{data.h1}</h1>
            <p className="mt-6 text-lg md:text-xl text-white/80 max-w-3xl leading-relaxed">{data.lead}</p>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-2 bg-white text-charcoal px-7 py-3.5 text-sm hover:bg-off-white transition-colors">Discuss a Transformation <ArrowRight size={16} /></Link>
          </Reveal>
        </div>
      </section>

      {data.blocks.map((b, i) => (
        <section key={i} className={`border-b border-[#E5E5E5] ${i % 2 ? "bg-off-white" : "bg-white"}`}>
          <div className="mx-auto max-w-7xl px-6 md:px-12 py-14 md:py-20"><Reveal><Block block={b} accent={accent} /></Reveal></div>
        </section>
      ))}

      {data.faqs?.length > 0 && (
        <section className="border-b border-[#E5E5E5] bg-white">
          <div className="mx-auto max-w-4xl px-6 md:px-12 py-14 md:py-20">
            <p className="overline mb-6" style={{ color: accent }}>FAQ</p>
            <Accordion type="single" collapsible>
              {data.faqs.map((f, i) => (
                <AccordionItem key={i} value={`f${i}`} className="border-b border-[#E5E5E5]">
                  <AccordionTrigger className="text-left font-serif text-lg text-deep-navy hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-charcoal/70 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      <section className="bg-deep-navy text-white">
        <div className="mx-auto max-w-7xl px-6 md:px-12 py-16 md:py-20 text-center">
          <h2 className="font-serif text-3xl md:text-5xl leading-tight max-w-3xl mx-auto">What should AI change inside your business?</h2>
          <Link to="/contact" className="mt-8 inline-flex items-center gap-2 bg-white text-deep-navy px-8 py-4 text-sm hover:bg-off-white transition-colors">Start a Conversation <ArrowRight size={16} /></Link>
        </div>
      </section>
    </div>
  );
}

// generic fetch hook
export function useDoc(url, deps) {
  const [data, setData] = useState(null);
  const [nf, setNf] = useState(false);
  useEffect(() => { setData(null); setNf(false); api.get(url).then(({ data }) => setData(data)).catch(() => setNf(true)); }, deps); // eslint-disable-line
  return { data, nf };
}
